import { TranslateService } from '@ngx-translate/core';
import { Box3, BufferGeometry, Euler, Matrix4, Quaternion, Vector3 } from 'three';
import { AttributeEvent } from '../configurator/product/interfaces/ProductEvents';
import { AttributeTypeENUM } from '../models/enums/ProductEnums';
import { Breakpoint, CollisionDescriptorUsageType } from '../models/interfaces/GenericInterfaces';
import { AttributeDTO, NumericChangeDTO } from '../models/product-models/DTOs/AttributeDTO';
import { AttributeSettingsDTO } from '../models/product-models/DTOs/AttributeSettingsDTO';
import { ProductBlueprintDTO } from '../models/product-models/DTOs/ProductBlueprintDTO';
import {
  Attribute,
  FlipChange,
  MaterialChange,
  ModelChange,
  NumericChange,
} from '../models/product-models/views/Attribute';
import { AttributeValue, FlipValue, MaterialValue, ModelValue } from '../models/product-models/views/AttributeValue';
import { CollisionDescriptor, OBBCollisionDescriptor } from '../models/product-models/views/CollisionDescriptor';
import { LocalizedText } from '../models/product-models/views/LocalizedText';
import { AttributeEventsENUM, Product, ProductEventsENUM } from '../models/product-models/views/Product';
import { TransformationData } from '../models/product-models/views/TransformationData';
import { Wall } from '../models/product-models/views/Wall';
import { WallMovementConstraint } from '../models/product-models/views/WallPositionConstraint';
import { absoluteVector, getLocalBoundingBoxFromDescriptor } from '../models/utils';
import { Observable } from 'rxjs';

export enum PreviewSide {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
  Back = 'back',
  Front = 'front',
}

declare global {
  interface Window {
    webkit?: any;
  }
}

export function colisionDescriptorsToBoundingBox(collisionDescriptors: CollisionDescriptor[]) {
  const finalBB = new Box3();
  for (const colisionDescriptor of collisionDescriptors) {
    if (colisionDescriptor instanceof OBBCollisionDescriptor) {
      const obb = colisionDescriptor.getOBB();

      const min = obb.halfSize.clone().multiplyScalar(-1).add(obb.center);
      const max = obb.halfSize.clone().add(obb.center);

      min.applyMatrix3(obb.rotation);
      max.applyMatrix3(obb.rotation);

      const bb = new Box3(min, max);

      finalBB.union(bb);
    }
  }
  return finalBB;
}

export function colisionDescriptorsToCenteredBoundingBox(collisionDescriptors: CollisionDescriptor[]) {
  const finalBB = new Box3();
  for (const colisionDescriptor of collisionDescriptors) {
    if (colisionDescriptor instanceof OBBCollisionDescriptor) {
      const obb = colisionDescriptor.getOBB();

      const min = obb.halfSize.clone().multiplyScalar(-1).add(obb.center);
      const max = obb.halfSize.clone().add(obb.center);

      const bb = new Box3(min, max);

      finalBB.union(bb);
    }
  }
  return finalBB;
}

/**
 * Rounds a number to a specific precision.
 * @param number - The number to be rounded.
 * @param precision - The precision value or how many leading zeros.
 * @returns The rounded number.
 */
export function precisionRound(number: number, precision: number) {
  const factor = Math.pow(10, precision);
  return Math.round(number * factor) / factor;
}

/**
 * Resets the uvs of a geometry.
 * @param geometry - Geometry to be modified.
 */
export function resetUVs(geometry: BufferGeometry) {
  const pos = geometry.getAttribute('position'),
    nor = geometry.getAttribute('normal'),
    uvs = geometry.getAttribute('uv');

  for (let i = 0; i < pos.count; i++) {
    let x = 0,
      y = 0;

    const nx = Math.abs(nor.getX(i)),
      ny = Math.abs(nor.getY(i)),
      nz = Math.abs(nor.getZ(i));

    // if facing X
    if (nx >= ny && nx >= nz) {
      x = pos.getY(i);
      y = pos.getZ(i);
    }

    // if facing Y
    if (ny >= nx && ny >= nz) {
      x = pos.getX(i);
      y = pos.getZ(i);
    }

    // if facing Z
    if (nz >= nx && nz >= ny) {
      x = pos.getX(i);
      y = pos.getY(i);
    }

    uvs.setXY(i, x, y);
  }
}

/**
 * Limits the length of a string and appends '...' if it exceeds the maximum length.
 * @param str - The input string to be truncated.
 * @param maxLength - The maximum allowed length for the string (default: 10).
 * @returns The truncated string with '...' appended if necessary.
 * @throws Throws an error if maxLength is less than 3.
 */
export function limitString(str: string, maxLength = 10): string {
  if (maxLength < 3) {
    throw new Error("Maximum length should be at least 3 to accommodate for '...'");
  }

  if (str.length > maxLength) {
    return str.substring(0, maxLength - 3) + '...';
  } else {
    return str;
  }
}

/** Creates a Guid.
 * @returns A new Guid.
 */
export function guid(): string {
  const tS4 = function () {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  return tS4() + tS4() + '-' + tS4() + '-' + tS4() + '-' + tS4() + '-' + tS4() + tS4() + tS4();
}

export const COPY_TO_SEPERATOR_KEYS = [';', ',', 'Enter', ' '];

export const IS_MOBILE: boolean = (() => {
  const userAgent = navigator.userAgent || navigator.vendor || (self as any).opera;
  let check = false;
  // eslint-disable-next-line
  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent,
    ) ||
    // eslint-disable-next-line no-useless-escape
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      userAgent.substr(0, 4),
    )
  ) {
    check = true;
  }
  return check;
})();

export const IS_FACEBOOK_BROWSER: boolean = (() => {
  const userAgent = navigator.userAgent || navigator.vendor;
  return userAgent.includes('FB') || userAgent.includes('FBAV') || userAgent.includes('Instagram');
})();

export const IS_ANDROID = /android/i.test(navigator.userAgent);

export const IS_IPAD =
  /(Mac|Macintosh)/.test(navigator.userAgent) && navigator.maxTouchPoints > 1 && !(self as any).MSStream;

export const IS_IOS =
  (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(self as any).MSStream) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
  IS_IPAD;

export const IS_WKWEBVIEW = Boolean(window.webkit && window.webkit.messageHandlers);

// If running in iOS Safari proper, and not within a WKWebView component instance, check for ARQL feature support.
// Otherwise, if running in a WKWebView instance, check for known ARQL compatible iOS browsers, including:
// Chrome (CriOS), Edge (EdgiOS), Firefox (FxiOS), Google App (GSA), DuckDuckGo (DuckDuckGo).
// All other iOS browsers / apps will fail by default.
export const IS_AR_QUICKLOOK_CANDIDATE = (() => {
  if (IS_IOS) {
    if (IS_IPAD) {
      return true;
    }
    if (!IS_WKWEBVIEW) {
      const tempAnchor = document.createElement('a');
      return Boolean(tempAnchor.relList && tempAnchor.relList.supports && tempAnchor.relList.supports('ar'));
    } else {
      return Boolean(/CriOS\/|EdgiOS\/|FxiOS\/|GSA\//.test(navigator.userAgent));
    }
  } else {
    return false;
  }
})();

export const HAS_WEBXR_DEVICE_API =
  navigator.xr != null && (self as any).XRSession != null && navigator.xr.isSessionSupported != null;

export const HAS_WEBXR_HIT_TEST_API =
  HAS_WEBXR_DEVICE_API && (self as any).XRSession.prototype.requestHitTestSource != null;

export const IS_WEBXR_AR_CANDIDATE = HAS_WEBXR_HIT_TEST_API;

export const assertIsArCandidate = () => {
  if (IS_WEBXR_AR_CANDIDATE) {
    return;
  }

  const missingApis = [];

  if (!HAS_WEBXR_DEVICE_API) {
    missingApis.push('WebXR Device API');
  }

  if (!HAS_WEBXR_HIT_TEST_API) {
    missingApis.push('WebXR Hit Test API');
  }

  throw new Error(`The following APIs are required for AR, but are missing in this browser: ${missingApis.join(', ')}`);
};

export function attributeIsParentOfAttributeValue(attribute: Attribute, attributeValue: AttributeValue) {
  return attribute.attributeValues.includes(attributeValue);
}

export function getAttributeEventType(event: AttributeEvent) {
  const attribute = event.data?.attribute;
  const attributeValue = event.data?.attributeValue;

  if (!attribute || !attributeValue) {
    return undefined;
  }
  if (attribute instanceof MaterialChange && attributeValue instanceof MaterialValue) {
    return AttributeEventsENUM.MaterialChangeEvent;
  }
  if (attribute instanceof ModelChange && attributeValue instanceof ModelValue) {
    return AttributeEventsENUM.ModelChangeEvent;
  }
  if (attribute instanceof NumericChange && typeof attributeValue === 'number') {
    return AttributeEventsENUM.NumericChangeEvent;
  }
  if (attribute instanceof FlipChange && attributeValue instanceof FlipValue) {
    return AttributeEventsENUM.FlipEvent;
  }
  if (attribute instanceof Attribute && attributeValue instanceof AttributeValue) {
    return ProductEventsENUM.AttributeChangeEvent;
  }
  return undefined;
}

export function getAttributeValuePairType(
  attribute: Attribute,
  attributeValue?: AttributeValue | number,
): AttributeTypeENUM | undefined {
  if (!attribute || !attributeValue) {
    return undefined;
  }
  if (attribute instanceof MaterialChange && attributeValue instanceof MaterialValue) {
    return AttributeTypeENUM.MaterialChange;
  }
  if (attribute instanceof ModelChange && attributeValue instanceof ModelValue) {
    return AttributeTypeENUM.ModelChange;
  }
  if (attribute instanceof NumericChange && typeof attributeValue === 'number') {
    return AttributeTypeENUM.NumericChange;
  }
  if (attribute instanceof FlipChange && attributeValue instanceof FlipValue) {
    return AttributeTypeENUM.FlipChange;
  }
  if (attribute instanceof Attribute && attributeValue instanceof AttributeValue) {
    return AttributeTypeENUM.BaseAttribute;
  }
  return undefined;
}

/**
 * Checks if a given date is older than a specified number of days from today.
 *
 * @param {Date | string} dateToCheck - The date to check. Can be a Date object or a date string.
 * @param {number} days - The number of days to check against.
 * @returns {boolean} Returns true if the date is older than the specified number of days, false otherwise.
 */
export function isDateOlderThan(dateToCheck: Date | string, days: number): boolean {
  const pastDate = new Date(dateToCheck); // Convert string to Date if necessary
  const currentDate = new Date();
  const daysInMilliseconds = days * 24 * 60 * 60 * 1000; // Convert days to milliseconds

  // Subtract days from the current date
  const dateThreshold = new Date(currentDate.getTime() - daysInMilliseconds);

  return pastDate < dateThreshold; // Return true if pastDate is older than dateThreshold
}

export function getBreakpoint(width: number): Breakpoint {
  if (width < Breakpoint.XS) return Breakpoint.XS;
  if (width < Breakpoint.SM) return Breakpoint.SM;
  if (width < Breakpoint.MD) return Breakpoint.MD;
  if (width < Breakpoint.LG) return Breakpoint.LG;
  return Breakpoint.XL;
}

export function getLocalizedString(texts: LocalizedText[], translateService: TranslateService): string | null {
  if (!texts) return null;
  if (typeof texts === 'string') return texts;

  const text = texts.find((text) => text.language === translateService.currentLang);
  return text ? text.text : null;
}

export function getProductsReferencedAttributeValueIds(
  seriesAttributeSettings: AttributeSettingsDTO[],
  seriesBlueprint: ProductBlueprintDTO,
) {
  const allFilterIds = [];
  for (const seriesAttributeSetting of seriesAttributeSettings) {
    const foundFilterIds = recursivelyFindProductsReferencedAttributeValueIds(
      seriesAttributeSetting,
      seriesBlueprint.attributes,
    );
    if (foundFilterIds) {
      for (const foundFilterId of foundFilterIds) {
        allFilterIds.push(foundFilterId);
      }
    }
  }
  return allFilterIds;
}

function recursivelyFindProductsReferencedAttributeValueIds(
  seriesAttributeSetting: AttributeSettingsDTO,
  attributes: AttributeDTO[],
): number[] | undefined {
  for (const attribute of attributes) {
    for (const attributeValue of attribute.attributeValues) {
      if (attributeValue.id === seriesAttributeSetting.value) {
        return attributeValue.attributeValueIds;
      }
      const foundFromChildren = recursivelyFindProductsReferencedAttributeValueIds(
        seriesAttributeSetting,
        attributeValue.attributes,
      );
      if (foundFromChildren) return foundFromChildren;
    }
  }
  return undefined;
}

export function getAttributeSettingsNameOrExternalIdsFromIds(
  attributeSettings: AttributeSettingsDTO[],
  productBlueprints: ProductBlueprintDTO[],
  attributeValueFilterIds: number[] = [],
  translateService: TranslateService,
) {
  const attributeSettingsExternalIds: AttributeSettingExternalId[] = [];

  for (const attributeSetting of attributeSettings) {
    for (const productBlueprint of productBlueprints) {
      const foundExternalIds = recursivelyFindAttributeNameOrExternalId(
        productBlueprint.attributes,
        attributeSetting,
        attributeValueFilterIds,
        translateService,
      );
      if (foundExternalIds) {
        attributeSettingsExternalIds.push(foundExternalIds);
        continue;
      }
    }
  }
  return attributeSettingsExternalIds;
}

export function getProductBlueprintsExternalId(productBlueprintId: number, productBlueprints: ProductBlueprintDTO[]) {
  const foundProductBlueprint = productBlueprints.find((productBlueprint) => {
    productBlueprint.id === productBlueprintId;
  });
  return foundProductBlueprint ? foundProductBlueprint.externalId : '';
}

export interface AttributeSettingExternalId {
  attributeExternalId: string;
  attributeValueExternalId?: string;
  numericValue?: string;
}

function recursivelyFindAttributeNameOrExternalId(
  attributes: AttributeDTO[],
  attributeSetting: AttributeSettingsDTO,
  attributeValueFilterIds: number[] = [],
  translateService: TranslateService,
): AttributeSettingExternalId | undefined {
  for (const attribute of attributes) {
    if (attribute.receiptSettings === undefined && attribute.isShown === false) continue;

    //if (attribute.receiptSettings !== undefined && attribute.receiptSettings.showOnReceipt === false) continue;
    if (
      attribute.id === attributeSetting.key &&
      attributeSetting.numericValue /* && attribute instanceof NumericChangeDTO */
    ) {
      const translation = getLocalizedString(
        attribute.name.map((name) => LocalizedText.fromDTO(name)),
        translateService,
      );
      return {
        attributeExternalId: translation ? translation : '',
        numericValue:
          precisionRound(attributeSetting.numericValue * (attribute as NumericChangeDTO)?.unitFactor, 2).toString() +
          ' ' +
          (attribute as NumericChangeDTO)?.unit,
      };
    }
    for (const attributeValue of attribute.attributeValues) {
      const jsonData = attribute.builderData ? JSON.parse(attribute.builderData) : undefined;
      if (!attributeValueFilterIds.includes(attributeValue.id)) {
        if (
          attribute.id === attributeSetting.key &&
          attributeValue.id === attributeSetting.value &&
          ((jsonData !== undefined && jsonData.showOnAdminPanel === true) ||
            jsonData === undefined ||
            jsonData.showOnAdminPanel === undefined)
        ) {
          const attrName = getLocalizedString(
            attribute.name.map((name) => LocalizedText.fromDTO(name)),
            translateService,
          );
          const attributeValueName = getLocalizedString(
            attributeValue.infoText.map((infoText) => LocalizedText.fromDTO(infoText)),
            translateService,
          );
          return {
            attributeExternalId: attrName ?? attribute.externalId,
            attributeValueExternalId: attributeValueName ?? attributeValue.externalId,
          };
        }
      }

      const recVal = recursivelyFindAttributeNameOrExternalId(
        attributeValue.attributes,
        attributeSetting,
        attributeValueFilterIds,
        translateService,
      );
      if (recVal) return recVal;
    }
  }
  return undefined;
}

/**
 * Compares two THREE.Vector3 vectors to see if they are equal within a certain threshold.
 * @param v1 - The first vector to compare.
 * @param v2 - The second vector to compare.
 * @param threshold - The maximum difference allowed between corresponding components of the vectors.
 * @returns boolean - True if the vectors are equal within the threshold, otherwise false.
 */
export function areVectorsEqual(v1: Vector3, v2: Vector3, threshold: number): boolean {
  return Math.abs(v1.x - v2.x) <= threshold && Math.abs(v1.y - v2.y) <= threshold && Math.abs(v1.z - v2.z) <= threshold;
}

export function getProductRelationshipTransformationData(
  productToPreview: Product,
  parentProduct: Product,
  side: PreviewSide,
  aligments: PreviewSide[] = [],
  finalProductRotation = [0, 0, 0],
  wall?: Wall,
  calculateParentChildrenBB = true,
  calculateObjectChildrenBB = true,
  gap = 0, // default gap is 0
) {
  if (wall === undefined) {
    wall = productToPreview.wall;
  }

  // Check to show product on previews that have wall with allowed utilities
  if (productToPreview.productBlueprint.utilityIds?.length) {
    if (wall === undefined) {
      return;
    }
    const wallUtilities = wall.getUtilities();
    const productUtilityIds = new Set(productToPreview.productBlueprint.utilityIds);

    if (!wallUtilities.some((item) => item.id && productUtilityIds.has(item.id.toString()))) {
      return;
    }
  }

  const eulerRotation = new Euler(finalProductRotation[0], finalProductRotation[1], finalProductRotation[2]);
  const rotationMatrix = new Matrix4().makeRotationFromEuler(eulerRotation);

  const parentBB = getLocalBoundingBoxFromDescriptor(
    parentProduct,
    CollisionDescriptorUsageType.SELF,
    calculateParentChildrenBB,
  );

  const size = new Vector3();
  parentBB.getSize(size);

  const previewBB = getLocalBoundingBoxFromDescriptor(
    productToPreview,
    CollisionDescriptorUsageType.SELF,
    calculateObjectChildrenBB,
  );

  previewBB.applyMatrix4(rotationMatrix);
  const position = new Vector3();

  switch (side) {
    case PreviewSide.Left: {
      position.set(parentBB.min.x - previewBB.max.x - gap, 0, 0);
      break;
    }
    case PreviewSide.Right: {
      position.set(parentBB.max.x - previewBB.min.x + gap, 0, 0);
      break;
    }
    case PreviewSide.Front: {
      position.set(0, 0, parentBB.max.z - previewBB.min.z + gap);
      break;
    }
    case PreviewSide.Back: {
      position.set(0, 0, parentBB.min.z - previewBB.max.z + gap);
      break;
    }
    case PreviewSide.Top: {
      position.set(0, 0, parentBB.max.y - previewBB.min.y + gap);
      break;
    }
    case PreviewSide.Bottom: {
      position.set(0, 0, parentBB.min.y - previewBB.max.y + gap);
      break;
    }
  }

  for (const aligmentSide of aligments) {
    switch (aligmentSide) {
      case PreviewSide.Back: {
        position.z = parentBB.min.z - previewBB.min.z;
        break;
      }
      case PreviewSide.Top: {
        position.y = parentBB.max.y - previewBB.max.y;
        break;
      }
      case PreviewSide.Bottom: {
        position.y = parentBB.min.y - previewBB.min.y;
        break;
      }
      case PreviewSide.Left: {
        position.x = parentBB.min.x - previewBB.min.x;
        break;
      }
      case PreviewSide.Right: {
        position.x = parentBB.max.x - previewBB.max.x;
        break;
      }
      case PreviewSide.Front: {
        position.z = parentBB.max.z - previewBB.max.z;
        break;
      }
    }
  }

  return new TransformationData(undefined, position.clone().toArray(), finalProductRotation);
}

export function removeIds(obj: any): any {
  if (Array.isArray(obj)) {
    // If it's an array, recursively call removeIds on each element
    return obj.map(removeIds);
  } else if (typeof obj === 'object' && obj !== null) {
    // If it's an object, iterate over its properties
    const newObj: any = {};
    for (const key in obj) {
      // eslint-disable-next-line no-prototype-builtins
      if (obj.hasOwnProperty(key) && key !== 'id') {
        // Recursively process the value of each property (except 'id')
        newObj[key] = removeIds(obj[key]);
      }
    }
    return newObj;
  }
  // If it's a primitive (string, number, etc.), return it as is
  return obj;
}

type AnyObject = { [key: string]: any };

export function assignIds<T extends AnyObject>(obj: T): T {
  let currentId = 1;
  function walk(node: any): any {
    if (node !== null && typeof node === 'object') {
      // Assign ID if not present
      if (node.id === undefined && 'id' in node) {
        node.id = currentId++;
      }

      for (const key of Object.keys(node)) {
        if (typeof node[key] === 'object' && node[key] !== null) {
          walk(node[key]);
        }
      }
    }
    return node;
  }

  return walk(obj);
}

export function isStringOnlyDigits(input: string): boolean {
  return /^\d+$/.test(input);
}

export function roundTo(num: number, places: number) {
  const factor = 10 ** places;
  return Math.round(num * factor) / factor;
}

export function getClipBox(
  wall: Wall,
  centeredBBSize: Vector3,
  centerOffset: Vector3,
  wallMovementConstraint: WallMovementConstraint,
  productToWallAlignQuaternion: Quaternion,
  productRotationAroundLinkQuaternion: Quaternion,
  linkVector: Vector3,
  productOffset: number,
) {
  const rotatedBB = centeredBBSize
    .clone()
    .applyQuaternion(productToWallAlignQuaternion)
    .applyQuaternion(productRotationAroundLinkQuaternion);

  //represent the size of the wall
  const minVector = wall.getMinVector();
  const maxVector = wall.getMaxVector();
  if (wallMovementConstraint.topOffset.direction) {
    maxVector.y = minVector.y + wallMovementConstraint.topOffset.value + rotatedBB.y;
  } else {
    maxVector.y -= wallMovementConstraint.topOffset.value;
  }
  if (wallMovementConstraint.bottomOffset.direction) {
    minVector.y = maxVector.y - wallMovementConstraint.bottomOffset.value - rotatedBB.y;
  } else {
    minVector.y += wallMovementConstraint.bottomOffset.value;
  }
  if (wallMovementConstraint.leftOffset.direction) {
    minVector.x = maxVector.x - wallMovementConstraint.leftOffset.value - rotatedBB.x;
  } else {
    minVector.x += wallMovementConstraint.leftOffset.value;
  }
  if (wallMovementConstraint.rightOffset.direction) {
    maxVector.x = minVector.x + wallMovementConstraint.rightOffset.value + rotatedBB.x;
  } else {
    maxVector.x -= wallMovementConstraint.rightOffset.value;
  }

  //represents the size of the object divided by 2 and rotated so that it is corectly linked to the wall
  const substractiveClippingVector = rotatedBB.clone().multiplyScalar(0.5);

  //absolute value of the clipping vector without z
  const absoluteSubstractiveClippingVector = absoluteVector(substractiveClippingVector);

  absoluteSubstractiveClippingVector.z = 0; //Math.abs(clippingVector.z);

  absoluteSubstractiveClippingVector.sub(
    centerOffset
      .clone()
      .multiply(new Vector3(1, 1, 1).sub(linkVector))
      .applyQuaternion(productToWallAlignQuaternion)
      .applyQuaternion(productRotationAroundLinkQuaternion),
  );

  //represents the size of the object divided by 2 and rotated so that it is corectly linked to the wall
  const additiveClippingVector = rotatedBB.clone().multiplyScalar(0.5);

  //absolute value of the clipping vector without z
  const absoluteAditiveClippingVector = absoluteVector(additiveClippingVector);
  absoluteAditiveClippingVector.z = 0; //Math.abs(clippingVector.z);

  absoluteAditiveClippingVector.add(
    centerOffset
      .clone()
      .multiply(new Vector3(1, 1, 1).sub(linkVector))
      .applyQuaternion(productToWallAlignQuaternion)
      .applyQuaternion(productRotationAroundLinkQuaternion),
  );

  minVector.add(absoluteSubstractiveClippingVector);
  maxVector.sub(absoluteAditiveClippingVector);

  minVector.z += productOffset;
  maxVector.z += productOffset;

  //the actual bounding box in which to clamp the object location
  return new Box3(minVector, maxVector);
}

export function removeNulls(obj: any): void {
  for (const key in obj) {
    if (obj[key] === null) {
      // Delete the property if the value is null
      delete obj[key];
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      // Recursively remove nulls from nested objects
      removeNulls(obj[key]);
      // If the object becomes empty after removing nulls, consider deleting it too
      if (Object.keys(obj[key]).length === 0 && !Array.isArray(obj[key])) {
        delete obj[key];
      }
    }
  }
}

export function arraysAreEqualBySet(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  const setB = new Set(b);
  for (const item of setA) {
    if (!setB.has(item)) return false;
  }
  return true;
}
//developed for camera recenter on bb change. ensure that only other triggers the resulting observable with latest values from both.
//both need to trigger atleast once after each emit of resulting observable
export function cycleLatestWithTrigger<A, B>(other$: Observable<B>) {
  return (source$: Observable<A>): Observable<[A, B]> =>
    new Observable<[A, B]>((subscriber) => {
      let latestA: A | undefined;
      let latestB: B | undefined;
      let seenA = false;
      let seenB = false;

      const reset = () => {
        seenA = false;
        seenB = false;
      };

      const subA = source$.subscribe({
        next: (a) => {
          latestA = a;
          seenA = true;
          // do NOT emit here, A is not the trigger
        },
        error: (e) => subscriber.error(e),
        complete: () => subscriber.complete(),
      });

      const subB = other$.subscribe({
        next: (b) => {
          latestB = b;
          seenB = true;

          // Only emit when triggered by B and both seen
          if (seenA && seenB) {
            subscriber.next([latestA!, latestB!]);
            reset();
          }
        },
        error: (e) => subscriber.error(e),
        complete: () => subscriber.complete(),
      });

      return () => {
        subA.unsubscribe();
        subB.unsubscribe();
      };
    });
}
